import { masterSupabase } from '../lib/supabase-master.js';

const accessCache = new Map();
const EXPIRY_SAFETY_MS = 30_000;

async function getAccessToken() {
  const { data, error } = await masterSupabase.auth.getSession();
  if (error) throw error;
  const token = data?.session?.access_token;
  if (!token) throw new Error('Anmeldung erforderlich');
  return token;
}

function validCachedEntry(entry) {
  return Boolean(entry?.viewerUrl && entry?.expiresAt && entry.expiresAt - EXPIRY_SAFETY_MS > Date.now());
}

async function getDocumentAccess(documentId, { forceRefresh = false } = {}) {
  const id = String(documentId || '').trim();
  if (!id) throw new Error('Dokument-ID fehlt');

  const cached = accessCache.get(id);
  if (!forceRefresh && validCachedEntry(cached)) return cached;

  const token = await getAccessToken();
  const response = await fetch(`/api/clara/documents/${encodeURIComponent(id)}/portal-view`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.document?.viewer_url) {
    throw new Error(payload?.error || payload?.message || 'Dokument ist nicht verfügbar oder nicht zugelassen');
  }

  const expiresAt = payload.document.expires_at
    ? new Date(payload.document.expires_at).getTime()
    : Date.now() + 4 * 60 * 1000;
  const entry = {
    viewerUrl: payload.document.viewer_url,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + 4 * 60 * 1000,
    filename: payload.document.filename || 'Dokument',
    contentType: payload.document.content_type || null,
  };
  accessCache.set(id, entry);
  return entry;
}

async function openDocument(documentId) {
  const target = window.open('', '_blank');
  if (target) target.opener = null;
  try {
    const access = await getDocumentAccess(documentId, { forceRefresh: true });
    if (target) target.location.replace(access.viewerUrl);
    else window.location.assign(access.viewerUrl);
    return access;
  } catch (error) {
    if (target && !target.closed) target.close();
    throw error;
  }
}

async function uploadDocument(file, metadata = {}) {
  if (!(file instanceof File) || file.size < 1) throw new Error('Datei fehlt');
  const token = await getAccessToken();
  const form = new FormData();
  form.append('file', file, file.name);
  for (const [key, value] of Object.entries(metadata || {})) {
    if (value !== null && value !== undefined && String(value).trim() !== '') form.append(key, String(value));
  }
  const response = await fetch('/api/clara/documents', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.document?.id) {
    throw new Error(payload?.error?.message || payload?.message || 'Dokument konnte nicht gespeichert werden');
  }
  invalidateDocumentAccess(payload.document.id);
  return payload.document;
}

async function deleteDocument(documentId) {
  const id = String(documentId || '').trim();
  if (!id) throw new Error('Dokument-ID fehlt');
  const token = await getAccessToken();
  const response = await fetch(`/api/clara/documents/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.deleted !== true) {
    throw new Error(payload?.error?.message || payload?.message || 'Dokument konnte nicht gelöscht werden');
  }
  invalidateDocumentAccess(id);
  return payload;
}

function invalidateDocumentAccess(documentId) {
  accessCache.delete(String(documentId || '').trim());
}

export const documentAccessService = {
  getDocumentAccess,
  openDocument,
  uploadDocument,
  deleteDocument,
  invalidateDocumentAccess,
};

export default documentAccessService;
